import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Shield } from 'lucide-react';

const MAX_SKIP_COUNT = 2;
const SESSION_DISMISSED_KEY = 'payzen_2fa_dismissed';

function getSessionDismissedKey(userId) {
  return userId ? `${SESSION_DISMISSED_KEY}_${userId}` : null;
}

/**
 * Mandatory 2FA for Live production users who have completed KYB.
 * Modal shows only after logout and re-login (not when switching Sandbox/Live).
 * User can skip at most 2 times per account; after each skip the modal closes for this session.
 */
export default function Mandatory2FAModal() {
  const { user, api, refreshUser } = useAuth();
  const { isLive } = useEnvironment();
  const navigate = useNavigate();
  const [skipping, setSkipping] = useState(false);
  const [dismissedForSession, setDismissedForSession] = useState(false);

  const skipCount = user?.two_fa_skip_count ?? 0;
  const canSkip = skipCount < MAX_SKIP_COUNT;
  const mustEnable = !canSkip;

  const kycBannerStillVisible = typeof sessionStorage !== 'undefined' && user?.id && sessionStorage.getItem(`payzen_kyc_approved_seen_${user.id}`) !== '1';

  const showModal =
    isLive &&
    user &&
    user.role !== 'admin' &&
    user.kyc_status === 'approved' &&
    !user.is_2fa_enabled &&
    !kycBannerStillVisible;

  const sessionDismissed = typeof sessionStorage !== 'undefined' && user?.id && sessionStorage.getItem(getSessionDismissedKey(user.id)) === '1';

  const handleSkip = useCallback(async () => {
    if (!canSkip) return;
    setSkipping(true);
    setDismissedForSession(true);
    try {
      const key = getSessionDismissedKey(user?.id);
      if (key) sessionStorage.setItem(key, '1');
      await api.post('/auth/skip-2fa-reminder');
      await refreshUser();
    } catch {
      setDismissedForSession(false);
      try {
        const key = getSessionDismissedKey(user?.id);
        if (key) sessionStorage.removeItem(key);
      } catch (_) {}
    } finally {
      setSkipping(false);
    }
  }, [canSkip, api, refreshUser, user?.id]);

  const handleEnable2FA = useCallback(() => {
    setDismissedForSession(true);
    const key = getSessionDismissedKey(user?.id);
    if (key) sessionStorage.setItem(key, '1');
    navigate('/settings?tab=2fa');
  }, [navigate, user?.id]);

  if (!showModal || dismissedForSession || sessionDismissed) return null;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => mustEnable && e.preventDefault()}
        onEscapeKeyDown={(e) => mustEnable && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <Shield className="w-5 h-5 text-[#0B6445]" />
            Two-factor authentication required
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {mustEnable
              ? 'You have skipped the maximum number of times. You must enable 2FA to continue using the platform in Live mode.'
              : 'For production (Live) accounts, two-factor authentication is mandatory. Please enable 2FA to secure your account. You may skip this reminder up to 2 times.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0 flex flex-col-reverse sm:flex-row">
          {canSkip && (
            <Button
              type="button"
              variant="outline"
              onClick={handleSkip}
              disabled={skipping}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            >
              {skipping ? '…' : `Skip for now (${MAX_SKIP_COUNT - skipCount} left)`}
            </Button>
          )}
          <Button
            type="button"
            className="bg-[#0B6445] hover:bg-[#095b3d] text-white"
            onClick={handleEnable2FA}
          >
            Enable 2FA
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
